import type { HexString } from "@/types/general.types";
import { errorLog, log } from "@/features/bridge/utils/log";
import { useWaitForTransaction } from "@/hooks/useWaitForTransaction.hook";
import { useUIStore } from "@/stores/ui-states.store";
import {
  useSwitchChain,
  useWriteContract as useWriteWagmiContract,
} from "wagmi";

interface UseMakeWagmiTxCallProps {
  ignorePendingTxState?: boolean; // If true then pending transactions won't trigger component updates
  autoSwitchChainId?: number; // Automatically switches chain to specified chain id if needed
  mainnetOptimized?: boolean; // If true, uses enhanced mainnet-compatible transaction waiting
  callback?: {
    onMutate?: () => void;
    onSigningError?: () => void;
    onSuccess?: (receiptHash: HexString) => void;
    onRevert?: (revertInfo: unknown) => void;
    onTimeout?: () => void;
    onFinally?: () => void;
  };
}

/**
 * A hook to manage contract write operations with enhanced features:
 * - Handles transaction timeouts and provides customizable callbacks for different transaction states.
 * - Simplifies transaction state management, UI updates, and logging.
 * - Supports automatic chain switching when required.
 */
export const useManagedWriteContract = ({
  callback,
  ignorePendingTxState = false,
  autoSwitchChainId,
  mainnetOptimized = false,
}: UseMakeWagmiTxCallProps) => {
  // Subscribes to specific state changes, ensuring components re-render only when relevant state updates occur
  // Manages global UI state efficiently for subscribed components
  const {
    pendingTxns,
    addPendingTxn,
    flushPendingTxn,
    isSigningPending,
    setIsSigningPending,
  } = useUIStore((state) => ({
    pendingTxns: state.pendingTxns,
    addPendingTxn: state.addPendingTxn,
    flushPendingTxn: state.flushPendingTxn,
    isSigningPending: state.isSigningPending,
    setIsSigningPending: state.setIsSigningPending,
  }));

  const { waitForTransactionWithTimeout } = useWaitForTransaction();
  const { switchChainAsync } = useSwitchChain();

  const { writeContractAsync } = useWriteWagmiContract({
    mutation: {
      onMutate: () => {
        log("Signing process started...");
        if (autoSwitchChainId)
          switchChainAsync({
            chainId: autoSwitchChainId,
          });
        setIsSigningPending(true);
        callback?.onMutate?.();
      },
      onError: (error) => {
        errorLog(error);
        setIsSigningPending(false);
        callback?.onSigningError?.();
      },
      onSuccess: async (response) => {
        setIsSigningPending(false);
        log("Signing success!");
        log("Waiting for transaction to complete...");

        // Add the transaction to the list of pending transactions.
        // If multiple transactions are initiated in quick succession, they will be appended to the array.
        if (!ignorePendingTxState) addPendingTxn(response);

        // Wait for tx, with enhanced mainnet compatibility if enabled
        await waitForTransactionWithTimeout({
          signingHash: response,
          timeoutMs: mainnetOptimized ? 120_000 : 60_000, // Longer timeout for mainnet
          usePollingFallback: mainnetOptimized, // Enable polling fallback for mainnet
          onSuccess: (receiptHash) => callback?.onSuccess?.(receiptHash),
          onRevert: callback?.onRevert,
          onTimeout: callback?.onTimeout,
          onFinally: () => {
            if (!ignorePendingTxState) flushPendingTxn(response); // Remove the transaction from pending transactions
            callback?.onFinally?.();
          },
        });
      },
    },
  });

  return {
    writeContractAsyncWithTimeout: writeContractAsync,
    pendingTxns,
    isSigningPending,
  };
};
